import {
  maximumRedeemableMemberPoints,
  memberPointDiscount,
} from "./loyalty";

export interface OrderAmendmentLine {
  orderItemId: string;
  quantity: number;
  unitPrice: number;
}

export interface OrderAmendmentChange {
  orderItemId: string;
  quantity: number;
}

export interface OrderAmendmentCalculation {
  newSubtotal: number;
  newDiscount: number;
  newPointsRedeemed: number;
  newTotal: number;
  returnedStock: number;
}

export function calculateOrderAmendment(input: {
  subtotal: number;
  deliveryFee: number;
  pointsRedeemed: number;
  pointValueRupiah: number;
  lines: readonly OrderAmendmentLine[];
  changes: readonly OrderAmendmentChange[];
}): OrderAmendmentCalculation {
  if (input.changes.length === 0) {
    throw new Error("ORDER_EDIT_EMPTY");
  }

  const lines = new Map(input.lines.map((line) => [line.orderItemId, line]));
  const seen = new Set<string>();
  let reduction = 0;
  let returnedStock = 0;
  let remainingLines = input.lines.length;

  for (const change of input.changes) {
    if (seen.has(change.orderItemId)) throw new Error("ORDER_EDIT_DUPLICATE_ITEM");
    seen.add(change.orderItemId);
    const line = lines.get(change.orderItemId);
    if (!line) throw new Error("ORDER_ITEM_NOT_FOUND");
    if (
      !Number.isSafeInteger(change.quantity) ||
      change.quantity < 0 ||
      change.quantity >= line.quantity
    ) {
      throw new Error("ORDER_EDIT_QUANTITY_INVALID");
    }
    const difference = line.quantity - change.quantity;
    returnedStock += difference;
    reduction += difference * line.unitPrice;
    if (change.quantity === 0) remainingLines -= 1;
  }

  if (remainingLines < 1) throw new Error("ORDER_EDIT_LAST_ITEM");
  const newSubtotal = input.subtotal - reduction;
  if (!Number.isSafeInteger(newSubtotal) || newSubtotal < 0) {
    throw new Error("ORDER_EDIT_TOTAL_INVALID");
  }
  const newPointsRedeemed = maximumRedeemableMemberPoints(
    input.pointsRedeemed,
    newSubtotal,
    input.pointValueRupiah,
  );
  const newDiscount = memberPointDiscount(
    newPointsRedeemed,
    input.pointValueRupiah,
  );
  return {
    newSubtotal,
    newDiscount,
    newPointsRedeemed,
    newTotal: newSubtotal + input.deliveryFee - newDiscount,
    returnedStock,
  };
}
